/**
 * O contrato da API de administração de contas.
 *
 * Mesma regra dos demais tipos escritos à mão: espelham campo a campo os records de
 * `interfaces/rest/v1/admin`. Quando um deles mudar no backend, o `tsc -b` quebra aqui e não em
 * produção.
 */

/** Os papéis do sistema, na ordem em que a tela os oferece. */
export const PAPEIS = [
  'ADMIN',
  'FISCAL',
  // Logo depois do fiscal, e antes do comitê: os dois são da fiscalização, e este NÃO libera
  // nada — é o registro de quem assina a ordem de serviço, que o TR cria sem dizer de quem é.
  'FISCAL_REQUISITANTE',
  'CAB_PRESIDENTE',
  // Logo depois do titular: quem concede um papel de comitê escolhe entre os dois lado a lado.
  'CAB_PRESIDENTE_SUPLENTE',
  'CAB_MEMBRO',
  'GMUD_SOLICITANTE',
  'GMUD_EXECUTOR',
  'LEITOR',
] as const

export type Papel = (typeof PAPEIS)[number]

/** Uma conta, como a tela de administração a vê. */
export interface UsuarioAdministrado {
  id: number
  email: string
  nome: string
  organizacaoSigla: string
  /** CONTRATANTE ou CONTRATADA — decide de que lado do contrato a pessoa está. */
  papelContratual: string
  papeis: Papel[]
  ativo: boolean
  /** Verdadeiro quando a conta entra pelo login institucional da Prefeitura. */
  entraVinculado: boolean
  deveTrocarSenha: boolean
  criadoEm: string
}

/** O corpo de `POST /api/v1/admin/usuarios`. */
export interface NovoUsuario {
  email: string
  nome: string
  organizacaoSigla: string
  papeis: Papel[]
  senhaProvisoria: string
}

/**
 * Uma coisa esperando o usuário logado.
 *
 * Derivada de estado + papel + lado do contrato pelo servidor, sem tabela própria. `quantos` nunca
 * é zero: contagem zero não vira linha, porque uma caixa de entrada que nunca esvazia deixa de ser
 * lida.
 */
export interface Pendencia {
  /** Identificador estável, para a tela decidir apresentação sem depender do texto. */
  codigo: string
  titulo: string
  ajuda: string
  /** Para onde o clique leva, com o recorte já aplicado. */
  endereco: string
  quantos: number
}

/** Qual lista de apoio a tela de administração está mexendo. */
export type ListaDeApoio = 'sistemas' | 'finalidades'

/**
 * Um item de lista de apoio como a administração o vê.
 *
 * `ativo` só existe aqui: as telas de USO consomem `/api/v1/referencias/...`, que devolve só os
 * ativos. É na administração que se reativa o que foi desativado — por isso ela precisa dos dois.
 */
export interface ItemNaAdministracao {
  id: number
  /** Identificador estável. Nunca muda: renomear é mudar o rótulo, não o código. */
  codigo: string
  rotulo: string
  ativo: boolean
}

/** Um dia em que o relógio do prazo do contrato não anda. */
export interface DiaNaoUtil {
  data: string
  rotulo: string
  abrangencia: string
  /** De onde veio o registro: seed do contrato, e-Niterói, ou cadastro manual. */
  fonte: string
  /** Alguém confirmou que a ferramenta que carimba o prazo também considera este dia. */
  conferido: boolean
  conferidoPorNome: string | null
}
